import { cn } from '@/utilities/ui'
import { Slot, Slottable } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'

/**
 * Wilson Property canonical Button component.
 *
 * Figma sources (file 4aIcONIT1Ga1nH3Xn9Xciz — verified 2026-07-22):
 *   button          2198:34408   type × state × size
 *   icon button     2198:34803   type × state × size (square)
 *   loading button  2198:34778   state × size
 *   link button     2198:34729   state × size
 *
 * Sizes (label box height / horizontal padding):
 *   Large   h40 px32 · Regular h36 px24 · Small h32 px16 · Mini h24 px8
 * Icon-only sizes (square):
 *   Large   40 · Regular 36 · Small 32 · Mini 24
 * Link button:
 *   Regular/Large/Small 30×24 — bare inline text, ZERO padding, height = line-height.
 *   Mini 26×16 (14/16).
 *
 * Label    Body/bodyMdAlt — Lato 400, font/size/base 16, line-height bound to
 *          font/size/lg (responsive 24 desktop / 22 tablet / 20 mobile via `--fs-lg`).
 *          size=Mini uses Body/bodySmAlt 14/16 (`text-body-sm`).
 * Geometry gap `gap/gap-1` 4px, radius `sm` 2px. Padding is NOT reduced when the
 *          button carries an icon (see the `iconPadding` escape hatch below).
 * Focus    ring 3px `theme/ring` #8cc64080 (destructive uses error-300 #fca5a5).
 * Shadow   `shadow/xs` is declared on the OUTLINE type only — filled types have none.
 *
 * Types (Figma `type` axis — there is no `secondary` type in the Figma set):
 *   default         — bg theme/accent #8cc640 / text theme/accent-foreground #000
 *   outline         — transparent, 1px theme/border #e0dfe0, shadow-xs, text #000
 *   outline-inverse — transparent, 1px theme/primary-foreground #fff, text #fff.
 *                     For dark surfaces (footer, hero overlays). Figma 2198:34537.
 *   ghost           — no bg / text #000
 *   link            — always underlined, text theme/foreground #000, no hover colour
 *                     change, and it RESETS the size box to zero padding / auto height.
 *   destructive     — bg theme/destructive #ef4444 / text #fff
 *
 * States: `active & hover` is a single Figma state — every non-destructive type swaps
 * its fill to theme/secondary #74cdde with black text (outline / outline-inverse also
 * drop their border); destructive darkens to #b91c1c. Disabled = opacity 50%.
 *
 * Hover affordance: Figma models hover as a discrete colour swap with NO motion, so the
 * canonical button deliberately does NOT carry the `.btn-underline` sliding-underline
 * effect. Bespoke buttons that still apply `btn-underline` by hand should drop it when
 * they migrate onto this component.
 *
 * Loading (2198:34778): spinner + label, pinned to the theme/secondary fill with black
 * text at full opacity. Use the `loading` prop — it also sets aria-busy and disables the
 * control.
 */
const buttonVariants = cva(
  // Base: Lato 16/24 regular, 2px radius, 4px gap, green focus ring.
  // NB: font-size is `text-base` (font/size/base 16px) rather than the `text-body-md`
  // token because tailwind-merge classifies `text-body-md` as a text-COLOUR utility and
  // silently drops it against each variant's `text-*` colour. The line-height still comes
  // from the token (`--fs-lg` → 24/22/20), which is what Body/bodyMdAlt binds to.
  'inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-sm text-base leading-[var(--fs-lg)] font-normal transition-[color,background-color,border-color,transform] duration-200 active:scale-[0.98] motion-reduce:active:scale-100 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*="size-"])]:size-4 [&_svg]:shrink-0 focus-visible:ring-[3px] focus-visible:ring-ring focus-visible:ring-offset-0 focus-visible:outline-none',
  {
    variants: {
      variant: {
        default: 'bg-accent text-accent-foreground hover:bg-secondary',
        outline:
          'border border-border bg-transparent text-foreground shadow-xs hover:border-transparent hover:bg-secondary',
        // Figma `type=outline inverse` (2198:34537 / icon 2198:34868) — for dark
        // surfaces. Border + label are theme/primary-foreground #ffffff; hover swaps to
        // the shared theme/secondary fill with black text and drops the border.
        'outline-inverse':
          'border border-primary-foreground bg-transparent text-primary-foreground hover:border-transparent hover:bg-secondary hover:text-accent-foreground',
        ghost: 'bg-transparent text-foreground hover:bg-secondary',
        // Figma link buttons are underlined at rest and identical on hover/active.
        link: 'text-foreground underline decoration-solid [text-underline-position:from-font]',
        // #b91c1c (theme/destructive:hover) and #fca5a5 (color/status/error-300) have no
        // token in globals.css yet — see the parity escalation list.
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-[#b91c1c] focus-visible:ring-[#fca5a5]',
      },
      size: {
        // ---- label sizes -------------------------------------------------------
        // default = Figma Button Regular — 36px tall, 24px sides, 8px ends
        default: 'h-9 px-6 py-2',
        // sm = Figma Button Small — 32px tall, 16px sides (label stays 16/24)
        sm: 'h-8 px-4 py-2',
        // lg = Figma Button Large — 40px tall, 32px sides
        lg: 'h-10 px-8 py-2',
        // mini = Figma Button Mini — 24px tall, 8px sides, label Body/bodySmAlt 14/16
        mini: 'h-6 px-2 py-1 text-body-sm leading-4',
        // ---- icon-only (square) sizes -----------------------------------------
        // icon = Figma Icon Button Regular (36). Kept as the default `icon` name so the
        // existing call sites are untouched.
        icon: 'size-9 p-0',
        'icon-lg': 'size-10 p-0', // Icon Button Large  — 40
        'icon-sm': 'size-8 p-0', // Icon Button Small — 32
        'icon-mini': 'size-6 p-0', // Icon Button Mini  — 24
        // clear = no size constraints — let the parent control layout
        clear: '',
      },
      /**
       * Symmetric 16px side padding when the button DIRECTLY contains an `<svg>`.
       *
       * The canonical Figma button never reduces its padding for an icon, so this is OFF
       * by default. It exists because Figma's pagination prev/next (2198:38852 /
       * 2198:38854) DO specify symmetric px-16 with a chevron, and `src/components/ui/
       * pagination.tsx` needs to express that without re-deriving the padding scale.
       */
      iconPadding: {
        true: 'has-[>svg]:px-4',
        false: '',
      },
      /**
       * Figma loading button 2198:34778 — full-opacity, pinned to the theme/secondary
       * fill. See the `loading` prop on <Button>, which also injects the spinner.
       */
      loading: {
        true: 'cursor-wait disabled:opacity-100',
        false: '',
      },
    },
    compoundVariants: [
      // The link button is not a box: Figma 2198:34730 is 30×24 (Mini 26×16) with zero
      // padding and height === line-height. Declared as a compound so it lands AFTER the
      // `size` classes and actually wins the tailwind-merge conflict.
      { variant: 'link', class: 'h-auto px-0 py-0 active:scale-100' },
      // Loading pins every non-destructive type to the theme/secondary fill + black text.
      {
        loading: true,
        variant: 'default',
        class: 'bg-secondary text-accent-foreground hover:bg-secondary',
      },
      {
        loading: true,
        variant: 'outline',
        class: 'border-transparent bg-secondary text-accent-foreground shadow-none',
      },
      {
        loading: true,
        variant: 'outline-inverse',
        class: 'border-transparent bg-secondary text-accent-foreground',
      },
      {
        loading: true,
        variant: 'ghost',
        class: 'bg-secondary text-accent-foreground',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
      iconPadding: false,
      loading: false,
    },
  },
)

/**
 * Busy indicator for `<Button loading>`. Local so the canonical button has no icon-pack
 * dependency; 16px to match `[&_svg]:size-4`, stroke inherits `currentColor`.
 */
const ButtonSpinner: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    aria-hidden="true"
    className={cn('size-4 shrink-0 animate-spin', className)}
    data-slot="button-spinner"
    fill="none"
    focusable="false"
    stroke="currentColor"
    strokeLinecap="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
)

export interface ButtonProps
  extends React.ComponentProps<'button'>, Omit<VariantProps<typeof buttonVariants>, 'loading'> {
  asChild?: boolean
  /**
   * Figma loading button (2198:34778). Renders a spinner before the label, applies the
   * busy styling, sets `aria-busy` and disables the control. When `asChild` is used the
   * `disabled` attribute is not valid on the rendered element, so `aria-disabled` and
   * `pointer-events-none` carry the semantics instead.
   */
  loading?: boolean
}

const Button: React.FC<ButtonProps> = ({
  asChild = false,
  children,
  className,
  disabled,
  iconPadding,
  loading = false,
  size,
  variant,
  ...props
}) => {
  const Comp = asChild ? Slot : 'button'
  const isDisabled = Boolean(disabled) || loading

  return (
    <Comp
      data-slot="button"
      {...props}
      aria-busy={loading || undefined}
      aria-disabled={asChild && isDisabled ? true : undefined}
      className={cn(
        buttonVariants({ className, iconPadding, loading, size, variant }),
        asChild && isDisabled && 'pointer-events-none opacity-50',
        asChild && loading && 'opacity-100',
      )}
      data-loading={loading || undefined}
      {...(asChild ? {} : { disabled: isDisabled })}
    >
      {loading ? (
        <>
          <ButtonSpinner />
          <Slottable>{children}</Slottable>
        </>
      ) : (
        children
      )}
    </Comp>
  )
}

export { Button, buttonVariants }
